import React, { useState, useEffect } from 'react';
import { CreditCardIcon, ArrowUturnLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation, usePagination } from '../../hooks';
import { paymentsAPI, adminAPI, returnsAPI } from '../../services/api';
import { Card, SectionHeader, StatusBadge, Badge, Pagination, LoadingPage, EmptyState, Button, Modal, Select, Tabs } from '../../components/common/UI';
import { format } from 'date-fns';
import OrderDetailModal from '../../components/order/OrderDetailModal';

export function PaymentsPage() {
  const { page, setPage } = usePagination();
  const { data, loading } = useQuery(() => paymentsAPI.history({ page, limit: 20 }), [page]);
  const payments = Array.isArray(data) ? data : (data?.rows || data?.data || []);
  const total = data?.pagination?.total || data?.count || data?.total || payments.length || 0;
  const pages = data?.pagination?.pages || (data?.limit ? Math.ceil(total / data.limit) : Math.ceil(total / 20) || 1);

  return (
    <div className="animate-in space-y-5">
      <SectionHeader title="Payments" subtitle={`${total} transactions`} />
      <Card>
        {loading ? <LoadingPage /> : payments.length === 0 ? (
          <EmptyState icon={CreditCardIcon} title="No payments found" />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Razorpay ID</th>
                  <th>Method</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id}>
                    <td><span className="font-mono text-xs">{p.razorpay_payment_id || p.razorpay_order_id || '—'}</span></td>
                    <td><Badge variant="gray">{p.method?.toUpperCase()}</Badge></td>
                    <td><span className="font-mono font-semibold">₹{Number(p.amount).toLocaleString()}</span></td>
                    <td><StatusBadge status={p.status} /></td>
                    <td className="text-surface-400 text-xs">{p.created_at ? format(new Date(p.created_at), 'dd MMM yyyy HH:mm') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} pages={pages} onPageChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}

export function PayoutsPage() {
  const { page, setPage } = usePagination();
  const { data, loading } = useQuery(() => adminAPI.payouts({ page, limit: 20 }), [page]);
  const payouts = Array.isArray(data) ? data : (data?.rows || data?.data || []);
  const total = data?.pagination?.total || data?.count || data?.total || payouts.length || 0;
  const pages = data?.pagination?.pages || (data?.limit ? Math.ceil(total / data.limit) : Math.ceil(total / 20) || 1);

  return (
    <div className="animate-in space-y-5">
      <SectionHeader title="Payouts" subtitle={`${total} payout records`} />
      <Card>
        {loading ? <LoadingPage /> : payouts.length === 0 ? (
          <EmptyState icon={CreditCardIcon} title="No payouts found" />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Business</th>
                  <th>Gross</th>
                  <th>Fee</th>
                  <th>Net</th>
                  <th>Transfer ID</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map(p => (
                  <tr key={p.id}>
                    <td className="text-sm text-surface-600">{p.business_id?.substring(0, 8)}…</td>
                    <td><span className="font-mono">₹{Number(p.gross_amount).toLocaleString()}</span></td>
                    <td><span className="font-mono text-danger-600">-₹{Number(p.platform_fee_amount).toLocaleString()}</span></td>
                    <td><span className="font-mono font-semibold text-success-700">₹{Number(p.net_amount).toLocaleString()}</span></td>
                    <td><span className="font-mono text-xs text-surface-400">{p.razorpay_transfer_id || '—'}</span></td>
                    <td><StatusBadge status={p.status} /></td>
                    <td className="text-surface-400 text-xs">{p.created_at ? format(new Date(p.created_at), 'dd MMM yy') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} pages={pages} onPageChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}

const RETURN_STATUSES = ['requested', 'approved', 'rejected', 'pickup_scheduled', 'picked_up', 'inspected', 'refund_initiated', 'refunded', 'replacement_sent', 'closed'];
const RETURN_STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'requested', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'refunded', label: 'Refunded' },
];

const ReturnActionModal = ({ ret, open, onClose, onSaved }) => {
  const [status, setStatus] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [rejectionNote, setRejectionNote] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [pickupDate, setPickupDate] = useState('');

  useEffect(() => {
    if (ret) {
      setStatus(ret.status || '');
      setAdminNote(ret.admin_note || '');
      setRejectionNote(ret.rejection_note || '');
      setRefundAmount(ret.refund_amount || '');
      setPickupDate(ret.pickup_date || '');
    }
  }, [ret]);

  const { mutate: updateReturn, loading } = useMutation(
    () => returnsAPI.update(ret.id, {
      status,
      admin_note: adminNote || undefined,
      rejection_note: rejectionNote || undefined,
      refund_amount: refundAmount || undefined,
      pickup_date: pickupDate || undefined,
    }),
    { successMsg: 'Return updated', onSuccess: onSaved }
  );

  if (!ret) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Manage Return Request"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" loading={loading} onClick={updateReturn}>Update</Button>
        </div>
      }
    >
      <div className="space-y-4">

        {/* Return Info */}
        <div className="rounded-xl bg-surface-50 p-4 space-y-1 text-sm">
          <p><span className="text-surface-400">Order:</span> <span className="font-mono">{ret.order_id?.substring(0, 12)}…</span></p>
          <p><span className="text-surface-400">Reason:</span> <span className="font-medium capitalize">{ret.reason?.replace(/_/g, ' ')}</span></p>
          <p><span className="text-surface-400">Resolution:</span> <Badge variant="blue">{ret.resolution}</Badge></p>
          {ret.description && <p><span className="text-surface-400">Description:</span> {ret.description}</p>}
        </div>

        {/* Status */}
        <Select label="Update Status" value={status} onChange={e => setStatus(e.target.value)}>
          {RETURN_STATUSES.map(s => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </Select>

        {/* Pickup Date  */}
        {status === 'pickup_scheduled' && (
          <div>
            <label className="label">Pickup Date</label>
            <input
              className="input"
              type="date"
              value={pickupDate}
              onChange={e => setPickupDate(e.target.value)}
            />
          </div>
        )}

        {/* Refund Amount */}
        {(status === 'refund_initiated' || status === 'refunded') && (
          <div>
            <label className="label">Refund Amount (₹)</label>
            <input
              className="input"
              type="number"
              value={refundAmount}
              onChange={e => setRefundAmount(e.target.value)}
              placeholder="0.00"
            />
          </div>
        )}

        {/* Rejection Note */}
        {status === 'rejected' && (
          <div>
            <label className="label">Rejection Note</label>
            <textarea
              className="input min-h-[60px]"
              value={rejectionNote}
              onChange={e => setRejectionNote(e.target.value)}
              placeholder="Rejection ka reason likho…"
            />
          </div>
        )}

        {/* Admin Note */}
        <div>
          <label className="label">
            Admin Note <span className="text-surface-400 font-normal">(internal)</span>
          </label>
          <textarea
            className="input min-h-[60px]"
            value={adminNote}
            onChange={e => setAdminNote(e.target.value)}
            placeholder="Internal note…"
          />
        </div>

      </div>
    </Modal>
  );
};

export function ReturnsPage() {
  const [statusFilter, setStatusFilter] = useState('requested');
  const [selected,     setSelected]     = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const { page, setPage, reset }        = usePagination();

  const { data, loading, refetch } = useQuery(
    () => returnsAPI.adminList({ page, limit: 20, status: statusFilter || undefined }),
    [page, statusFilter]
  );

  const returns = data?.data || [];
  const total = data?.pagination?.total || 0;
  const pages = data?.pagination?.pages || 1;

  return (
    <div className="animate-in space-y-5">
      <SectionHeader title="Return Requests" subtitle={`${total} returns`} />
      <Card>
        <div className="mb-5">
          <Tabs
            tabs={RETURN_STATUS_TABS}
            active={statusFilter}
            onChange={v => { setStatusFilter(v); reset(); }}
          />
        </div>

        {loading ? <LoadingPage /> : returns.length === 0 ? (
          <EmptyState icon={ArrowUturnLeftIcon} title="No returns found" />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Reason</th>
                  <th>Resolution</th>
                  <th>Refund</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {returns.map(r => (
                  <tr key={r.id}>
                    <td>
                      <button 
                        onClick={() => setSelectedOrderId(r.order_id)}
                        className="font-mono text-xs text-brand-700 hover:underline"
                      >
                        {r.order_id?.substring(0, 10)}…
                      </button>
                    </td>
                    <td><span className="text-sm capitalize">{r.reason?.replace(/_/g, ' ')}</span></td>
                    <td><Badge variant="blue">{r.resolution}</Badge></td>
                    <td className="font-mono text-sm">
                      {r.refund_amount ? `₹${Number(r.refund_amount).toLocaleString()}` : '—'}
                    </td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="text-surface-400 text-xs">
                      {r.created_at ? format(new Date(r.created_at), 'dd MMM yy') : '—'}
                    </td>
                    <td>
                      <button
                        onClick={() => setSelected(r)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-50 text-brand-700 hover:bg-brand-100 transition-colors"
                      >
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} pages={pages} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <ReturnActionModal
        ret={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onSaved={() => { refetch(); setSelected(null); }}
      />

      <OrderDetailModal
        orderId={selectedOrderId}
        open={!!selectedOrderId}
        hideUpdateSection={true}
        onClose={() => setSelectedOrderId(null)}
      />
    </div>
  );
}